/**
 * AdLandingPage.tsx  —  Incubator Tech Limited
 *
 * Executive-grade Facebook/LinkedIn ad landing page.
 * Design language: McKinsey.com-inspired — white-dominant, editorial serif
 * headlines, restrained navy accent, generous whitespace, zero dark UI.
 *
 * All colours reference tailwind.config.js tokens:
 *   itl-navy  (#0D1B2A)  — primary dark / headings
 *   itl-blue  (#0057FF)  — accent / CTA
 *   itl-slate (#2C3E50)  — secondary text
 *   itl-cream (#F7F7F5)  — warm off-white backgrounds
 *
 * All fonts reference tailwind.config.js tokens:
 *   font-serif  → 'Playfair Display'
 *   font-sans   → 'Libre Franklin'
 *
 * NO hardcoded hex values or font-family strings anywhere in this file.
 *
 * Dependencies: react, framer-motion, react-helmet-async,
 *               your existing contactApi RTK slice.
 */

import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useSubmitContactMutation } from "../../app/api/contactApi";
import type { ProjectType, BudgetRange } from "../../app/api/contactApi";

// ─── Types ─────────────────────────────────────────────────────────────────
interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  projectType: ProjectType;
  budget: BudgetRange;
  timeline: string;
  message: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────
const PROJECT_TYPES: Exclude<ProjectType, "">[] = [
  "Web Development",
  "Mobile App",
  "Tech Product",
  "Consulting",
  "Other",
];

const BUDGETS: Exclude<BudgetRange, "">[] = [
  "< ₦1M",
  "₦1M – ₦5M",
  "₦5M – ₦20M",
  "₦20M+",
  "Let's discuss",
];

const STATS = [
  { value: "50+", label: "Projects Delivered" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "5+", label: "Years in Business" },
  { value: "< 24h", label: "Response Time" },
];

const SERVICES = [
  {
    num: "01",
    title: "Web Applications",
    desc: "Scalable, performant platforms engineered for growth — from MVPs to enterprise systems. React, Next.js, Node.js.",
  },
  {
    num: "02",
    title: "Mobile Apps",
    desc: "Cross-platform iOS & Android applications that delight users and drive measurable retention outcomes.",
  },
  {
    num: "03",
    title: "Tech Products",
    desc: "End-to-end SaaS and digital product builds — like our own Launch Kit Naija — from concept to market.",
  },
  {
    num: "04",
    title: "Tech Consulting",
    desc: "Architecture reviews, CTO advisory, vendor selection, and strategic technology roadmapping for your board.",
  },
];

const WHY_US = [
  "World-class tech stack built for resilience",
  "Fixed-price proposals — no surprise invoices",
  "Delivery in record time, without cutting corners",
  "Nigerian-owned, globally competitive standards",
];

// ─── Helpers ────────────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-sans text-[10px] font-bold uppercase tracking-[0.28em] mb-3 text-itl-blue">
      {children}
    </p>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-sans text-[10px] font-bold uppercase tracking-[0.22em] text-gray-400">
        {label}
        {required && <span className="ml-1 text-itl-blue">*</span>}
      </label>
      {children}
    </div>
  );
}

// inputClass: focus ring uses itl-blue via Tailwind's focus: variant
const inputClass =
  "w-full font-sans bg-transparent border-b border-gray-200 py-3 text-[14px] text-itl-slate placeholder-gray-300 focus:outline-none focus:border-itl-blue transition-colors duration-200";

// ─── Component ──────────────────────────────────────────────────────────────
export default function AdLandingPage() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
  });

  const [submitContact, { isLoading, isSuccess, isError, error }] =
    useSubmitContactMutation();

  const formRef = useRef<HTMLDivElement>(null);

  function update(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        phone: form.phone || undefined,
        projectType: form.projectType || undefined,
        budget: form.budget || undefined,
        timeline: form.timeline || undefined,
        message: form.message,
      }).unwrap();
    } catch {
      // isError drives the UI
    }
  }

  const errorMessage = (() => {
    if (!error) return null;
    if ("data" in error) {
      const d = error.data as { error?: string };
      return d?.error ?? "Something went wrong. Please try again.";
    }
    return "Network error. Please check your connection and try again.";
  })();

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <>
      {/* ── SEO / OG ── */}
      <Helmet>
        <title>
          World-Class Web & Mobile Development in Nigeria | Incubator Tech
          Limited
        </title>
        <meta
          name="description"
          content="Nigeria's leading web and mobile app development firm. Scalable, high-performance digital products — from MVPs to enterprise platforms — delivered in record time. Get a free proposal today."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Build Your Digital Product with Nigeria's Premier Tech Team"
        />
        <meta
          property="og:description"
          content="Web apps. Mobile apps. Tech products. Fixed-price quotes. Delivered in record time. Incubator Tech Limited."
        />
        <meta property="og:image" content="/images/og-ad.jpg" />
        <meta property="og:url" content="https://incubatortech.org/start" />
        <meta property="og:site_name" content="Incubator Tech Limited" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Build Your Digital Product with Nigeria's Premier Tech Team"
        />
        <meta
          name="twitter:description"
          content="Web apps. Mobile apps. Tech products. Fixed-price quotes. Delivered in record time."
        />
        <meta name="twitter:image" content="/images/og-ad.jpg" />
        {/* Exclude from organic index — ad landing page */}
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://incubatortech.org/start" />
      </Helmet>

      <div className="bg-white font-sans min-h-screen">

        {/* ══════════════════════════════════════════════
            HERO — left: copy + stats / right: form
        ══════════════════════════════════════════════ */}
        <section className="border-b border-gray-200">
          {/* Top accent bar */}
          <div className="h-[3px] w-full bg-itl-blue" />

          <div className="max-w-screen-xl mx-auto px-6 lg:px-16 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-14 lg:gap-20 items-start">

            {/* ── Left: headline + stats ── */}
            <div>
              {/* Wordmark */}
              <div className="flex items-center gap-2.5 mb-12">
                <div className="w-7 h-7 bg-itl-blue flex items-center justify-center shrink-0">
                  <span className="text-white text-[9px] font-black tracking-tight font-sans">
                    IT
                  </span>
                </div>
                <span className="font-sans text-[13px] font-semibold tracking-wide text-itl-navy">
                  Incubator Tech Limited
                </span>
              </div>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-sans text-[10px] font-bold uppercase tracking-[0.3em] mb-4 text-itl-blue"
              >
                Nigeria's Premier Technology Partner
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.18 }}
                className="font-serif text-[42px] lg:text-[58px] font-light leading-[1.06] mb-6 text-itl-navy"
              >
                Your idea deserves
                <br />
                <em className="text-itl-blue not-italic italic">world-class</em>{" "}
                execution.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="font-sans text-[15px] text-itl-slate leading-relaxed mb-8 max-w-md"
              >
                We build web applications, mobile apps, and technology products
                for Nigerian and global businesses — using a world-class tech
                stack, with fixed-price proposals and delivery in record time.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <button
                  onClick={scrollToForm}
                  className="px-8 py-3.5 bg-itl-navy text-white font-sans text-[11px] font-bold uppercase tracking-widest transition-opacity duration-200 hover:opacity-80"
                >
                  Get a Free Proposal →
                </button>
                <a
                  href="mailto:hello@incubatortech.org"
                  className="px-8 py-3.5 border border-gray-300 text-itl-navy font-sans text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 text-center hover:border-itl-navy"
                >
                  hello@incubatortech.org
                </a>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.75 }}
                className="font-sans text-[11px] text-gray-400 mt-4"
              >
                No commitment. Response within one business day.
              </motion.p>

              {/* Stats */}
              <div className="mt-12 pt-10 border-t border-gray-200 grid grid-cols-2 lg:grid-cols-4 gap-8">
                {STATS.map((s, i) => (
                  <FadeUp key={s.label} delay={0.65 + i * 0.07}>
                    <p className="font-serif text-3xl font-light mb-1 text-itl-navy">
                      {s.value}
                    </p>
                    <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                      {s.label}
                    </p>
                  </FadeUp>
                ))}
              </div>
            </div>

            {/* ── Right: form ── */}
            <div ref={formRef} className="scroll-mt-8">
              <div className="p-8 lg:p-10 border border-gray-200 bg-itl-cream">
                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-12 text-center"
                  >
                    <div className="w-12 h-12 bg-itl-blue flex items-center justify-center mx-auto mb-5">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="font-serif text-2xl font-light mb-3 text-itl-navy">
                      Brief received.
                    </h3>
                    <p className="font-sans text-gray-500 text-[13px] leading-relaxed max-w-xs mx-auto">
                      We'll review your project and be in touch within one
                      business day. A confirmation email is on its way.
                    </p>
                  </motion.div>
                ) : (
                  <>
                    <Eyebrow>Request a Free Proposal</Eyebrow>
                    <h2 className="font-serif text-2xl font-light mb-7 leading-snug text-itl-navy">
                      Tell us about your project.
                    </h2>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Field label="Full Name" required>
                          <input
                            type="text"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            required
                            placeholder="Emeka Okafor"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Email Address" required>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            required
                            placeholder="emeka@company.ng"
                            className={inputClass}
                          />
                        </Field>
                      </div>

                      {/* Company + Phone */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Field label="Company / Organisation">
                          <input
                            type="text"
                            value={form.company}
                            onChange={(e) => update("company", e.target.value)}
                            placeholder="Acme Fintech Ltd"
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Phone Number">
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            placeholder="+234 812 345 6789"
                            className={inputClass}
                          />
                        </Field>
                      </div>

                      {/* Project type */}
                      <Field label="Type of Project">
                        <div className="flex flex-wrap gap-2 mt-1">
                          {PROJECT_TYPES.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => update("projectType", type)}
                              className={`px-3.5 py-1.5 font-sans text-[10px] font-bold uppercase tracking-widest border transition-colors duration-150 ${
                                form.projectType === type
                                  ? "bg-itl-navy border-itl-navy text-white"
                                  : "bg-transparent border-gray-300 text-gray-500 hover:border-itl-navy hover:text-itl-navy"
                              }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {/* Budget */}
                      <Field label="Budget Range">
                        <div className="flex flex-wrap gap-2 mt-1">
                          {BUDGETS.map((b) => (
                            <button
                              key={b}
                              type="button"
                              onClick={() => update("budget", b)}
                              className={`px-3.5 py-1.5 font-sans text-[10px] font-bold uppercase tracking-widest border transition-colors duration-150 ${
                                form.budget === b
                                  ? "bg-itl-blue border-itl-blue text-white"
                                  : "bg-transparent border-gray-300 text-gray-500 hover:border-itl-blue hover:text-itl-blue"
                              }`}
                            >
                              {b}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {/* Timeline */}
                      <Field label="Desired Timeline">
                        <input
                          type="text"
                          value={form.timeline}
                          onChange={(e) => update("timeline", e.target.value)}
                          placeholder="e.g. Launch in Q3 2025"
                          className={inputClass}
                        />
                      </Field>

                      {/* Message */}
                      <Field label="Project Brief" required>
                        <textarea
                          value={form.message}
                          onChange={(e) => update("message", e.target.value)}
                          required
                          rows={4}
                          placeholder="Describe what you're building, the problem it solves, and any constraints we should know about…"
                          className={`${inputClass} resize-none`}
                        />
                      </Field>

                      {/* Error banner */}
                      {isError && errorMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="px-4 py-3 font-sans text-[13px] text-red-700 leading-relaxed bg-red-50 border border-red-100"
                        >
                          {errorMessage}
                        </motion.div>
                      )}

                      {/* Submit */}
                      <div className="pt-1">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="w-full py-4 bg-itl-navy text-white font-sans text-[11px] font-bold uppercase tracking-widest transition-opacity duration-200 hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                        >
                          {isLoading ? (
                            <>
                              <svg
                                className="animate-spin w-4 h-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                />
                              </svg>
                              Sending…
                            </>
                          ) : (
                            "Send Brief & Get Free Proposal →"
                          )}
                        </button>
                        <p className="font-sans text-[11px] text-gray-400 mt-3 text-center">
                          No commitment required. We respond within one business day.
                        </p>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            SERVICES — 4-col ruled grid
        ══════════════════════════════════════════════ */}
        <section className="py-20 bg-itl-cream border-b border-gray-200">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-16">
            <FadeUp className="mb-12">
              <Eyebrow>What We Build</Eyebrow>
              <h2 className="font-serif text-[34px] font-light leading-tight max-w-lg text-itl-navy">
                End-to-end digital solutions for ambitious organisations.
              </h2>
            </FadeUp>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-gray-200">
              {SERVICES.map((s, i) => (
                <FadeUp
                  key={s.num}
                  delay={i * 0.07}
                  className={`bg-white p-8 h-full ${
                    i < SERVICES.length - 1 ? "border-r border-gray-200" : ""
                  }`}
                >
                  <p className="font-sans text-[11px] font-bold uppercase tracking-widest mb-5 text-itl-blue">
                    {s.num}
                  </p>
                  <h3 className="font-serif text-xl font-light mb-3 leading-snug text-itl-navy">
                    {s.title}
                  </h3>
                  <p className="font-sans text-[13px] text-itl-slate leading-relaxed">
                    {s.desc}
                  </p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            WHY INCUBATOR TECH — 2-col
        ══════════════════════════════════════════════ */}
        <section className="py-20 border-b border-gray-200">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <Eyebrow>Why Incubator Tech</Eyebrow>
              <h2 className="font-serif text-[34px] font-light leading-tight mb-6 text-itl-navy">
                Built for the demands of modern Nigerian business.
              </h2>
              <p className="font-sans text-[14px] text-itl-slate leading-relaxed max-w-md">
                We combine global technology standards with an intimate
                understanding of the Nigerian business environment to deliver
                solutions that actually work — on time and within budget.
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="flex flex-col">
                {WHY_US.map((point) => (
                  <div
                    key={point}
                    className="flex items-start gap-4 py-5 border-b border-gray-200"
                  >
                    <div className="w-5 h-5 bg-itl-blue/10 flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 text-itl-blue"
                        fill="none"
                        viewBox="0 0 12 12"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2 6l3 3 5-5"
                        />
                      </svg>
                    </div>
                    <p className="font-sans text-[14px] text-itl-slate leading-relaxed">
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            PROCESS — 3-step
        ══════════════════════════════════════════════ */}
        <section className="py-20 bg-itl-cream border-b border-gray-200">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-16">
            <FadeUp className="mb-12">
              <Eyebrow>What Happens Next</Eyebrow>
              <h2 className="font-serif text-[34px] font-light leading-tight text-itl-navy">
                A clear, three-step process.
              </h2>
            </FadeUp>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200">
              {[
                {
                  n: "1",
                  title: "We review your brief",
                  desc: "Our team reads your project description and does initial research on your market and requirements — before we even speak.",
                },
                {
                  n: "2",
                  title: "Discovery call",
                  desc: "A focused 30-minute call to ask the right questions, understand your goals, and clarify any constraints or timelines.",
                },
                {
                  n: "3",
                  title: "Fixed-price proposal",
                  desc: "You receive a clear document: approach, timeline, and a fixed price. No surprises, no ambiguity, no obligation.",
                },
              ].map((s, i) => (
                <FadeUp key={s.n} delay={i * 0.08}>
                  <div className="bg-white p-10 h-full">
                    {/* Large step number — itl-blue at low opacity for decorative use */}
                    <p className="font-serif text-5xl font-light mb-6 text-itl-blue/20">
                      {s.n}
                    </p>
                    <h3 className="font-serif text-xl font-light mb-3 text-itl-navy">
                      {s.title}
                    </h3>
                    <p className="font-sans text-[13px] text-itl-slate leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            BOTTOM CTA — navy band
        ══════════════════════════════════════════════ */}
        <section className="py-20 px-6 lg:px-16 bg-itl-navy">
          <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <FadeUp>
              <p className="font-sans text-[10px] font-bold uppercase tracking-[0.28em] mb-3 text-itl-blue">
                Ready to Build?
              </p>
              <h2 className="font-serif text-[34px] lg:text-[42px] font-light text-white leading-tight max-w-xl">
                Let's start a conversation about your project.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1} className="shrink-0">
              <div className="flex flex-col items-start lg:items-center gap-4">
                <button
                  onClick={scrollToForm}
                  className="px-10 py-4 bg-itl-blue text-white font-sans text-[11px] font-bold uppercase tracking-widest transition-opacity duration-200 hover:opacity-80"
                >
                  Get Free Proposal →
                </button>
                <div className="flex flex-col gap-1">
                  <a
                    href="mailto:hello@incubatortech.org"
                    className="font-sans text-[12px] text-white/50 hover:text-white/80 transition-colors"
                  >
                    hello@incubatortech.org
                  </a>
                  <a
                    href="tel:+2347052499758"
                    className="font-sans text-[12px] text-white/50 hover:text-white/80 transition-colors"
                  >
                    +234 (0) 705 249 9758
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* ══════════════════════════════════════════════
            FOOTER
        ══════════════════════════════════════════════ */}
        <footer className="py-8 px-6 lg:px-16 border-t border-gray-200">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-sans text-[11px] text-gray-400">
              © {new Date().getFullYear()} Incubator Tech Limited. All rights reserved.
            </p>
            <div className="flex gap-6">
              {[
                { label: "Privacy Policy", href: "/privacy" },
                { label: "Terms", href: "/terms" },
                { label: "Main Site", href: "/" },
              ].map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  className="font-sans text-[11px] text-gray-400 hover:text-itl-slate transition-colors"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}